import type {
  ApiRequestBody,
  ApiResponseBody,
  ApiSearchQuery,
} from '@/lib/api/api-type-helper';

// ---------------------------------------------------------------------------
// List API Types
// ---------------------------------------------------------------------------

export type GetTicketListQuery = ApiSearchQuery<'/v2/pn-force/tickets', 'get'>;
export type GetTicketListResponse = ApiResponseBody<
  '/v2/pn-force/tickets',
  'get'
>;
export type TicketListItem = GetTicketListResponse['items'][0];

export type TicketOrderBy = NonNullable<GetTicketListQuery>['orderBy'];

// ---------------------------------------------------------------------------
// Count API Types
// ---------------------------------------------------------------------------

export type GetTicketCountQuery = ApiSearchQuery<
  '/v2/pn-force/tickets/count',
  'get'
>;
export type GetTicketCountResponse = ApiResponseBody<
  '/v2/pn-force/tickets/count',
  'get'
>;

// ---------------------------------------------------------------------------
// Detail API Types
// ---------------------------------------------------------------------------

export type GetTicketResponse = ApiResponseBody<
  '/v2/pn-force/tickets/{id}',
  'get'
>;

// ---------------------------------------------------------------------------
// Post Create API Types
// ---------------------------------------------------------------------------

export type PostTicketRequest = ApiRequestBody<'/v2/pn-force/tickets', 'post'>;
export type PostTicketResponse = ApiResponseBody<
  '/v2/pn-force/tickets',
  'post'
>;

// ---------------------------------------------------------------------------
// Dev Scope (Production 詳細 > 開発スコープ編集) API Types
// ---------------------------------------------------------------------------

/** 開発スコープ追加候補 Ticket 一覧の query（productionId は path 由来で固定注入）。 */
export type GetAssignableTicketsQuery = ApiSearchQuery<
  '/v2/pn-force/productions/{id}/assignable-tickets',
  'get'
>;
export type GetAssignableTicketsResponse = ApiResponseBody<
  '/v2/pn-force/productions/{id}/assignable-tickets',
  'get'
>;
export type AssignableTicketItem = GetAssignableTicketsResponse['items'][0];

/** 開発スコープへ Ticket を一括追加する body。 */
export type PostDevScopeTicketsRequest = ApiRequestBody<
  '/v2/pn-force/productions/{id}/dev-scope-tickets',
  'post'
>;
export type PostDevScopeTicketsResponse = ApiResponseBody<
  '/v2/pn-force/productions/{id}/dev-scope-tickets',
  'post'
>;

export type DeleteDevScopeTicketResponse = ApiResponseBody<
  '/v2/pn-force/productions/{id}/dev-scope-tickets/{ticketId}',
  'delete'
>;

// ---------------------------------------------------------------------------
// Ticket Enums (BE 定義 8 / 6 / 4 / 4 値）
// ---------------------------------------------------------------------------

export const TicketKind = {
  NEW_FEATURE: 'NEW_FEATURE',
  ENHANCEMENT: 'ENHANCEMENT',
  BUG_FIX: 'BUG_FIX',
  REFACTORING: 'REFACTORING',
  TECH_DEBT: 'TECH_DEBT',
  RESEARCH: 'RESEARCH',
  OPS: 'OPS',
  OTHER: 'OTHER',
} as const;
export type TicketKind = (typeof TicketKind)[keyof typeof TicketKind];

const TICKET_KIND_VALUES: ReadonlySet<string> = new Set(
  Object.values(TicketKind),
);
export function isTicketKind(
  value: string | null | undefined,
): value is TicketKind {
  return value != null && TICKET_KIND_VALUES.has(value);
}

export const TicketStatus = {
  OPEN: 'OPEN',
  IN_PROGRESS: 'IN_PROGRESS',
  IN_REVIEW: 'IN_REVIEW',
  DONE: 'DONE',
  CLOSED: 'CLOSED',
  ARCHIVED: 'ARCHIVED',
} as const;
export type TicketStatus = (typeof TicketStatus)[keyof typeof TicketStatus];

const TICKET_STATUS_VALUES: ReadonlySet<string> = new Set(
  Object.values(TicketStatus),
);
export function isTicketStatus(
  value: string | null | undefined,
): value is TicketStatus {
  return value != null && TICKET_STATUS_VALUES.has(value);
}

/** 一覧画面のステータスフィルタ用。ドメイン値 + "ALL"。 */
export type TicketStatusFilter = TicketStatus | 'ALL';

export const TicketPriority = {
  P0: 'P0',
  P1: 'P1',
  P2: 'P2',
  P3: 'P3',
} as const;
export type TicketPriority =
  (typeof TicketPriority)[keyof typeof TicketPriority];

const TICKET_PRIORITY_VALUES: ReadonlySet<string> = new Set(
  Object.values(TicketPriority),
);
export function isTicketPriority(
  value: string | null | undefined,
): value is TicketPriority {
  return value != null && TICKET_PRIORITY_VALUES.has(value);
}

export const TicketSeverity = {
  CRITICAL: 'CRITICAL',
  HIGH: 'HIGH',
  MEDIUM: 'MEDIUM',
  LOW: 'LOW',
} as const;
export type TicketSeverity =
  (typeof TicketSeverity)[keyof typeof TicketSeverity];

const TICKET_SEVERITY_VALUES: ReadonlySet<string> = new Set(
  Object.values(TicketSeverity),
);
export function isTicketSeverity(
  value: string | null | undefined,
): value is TicketSeverity {
  return value != null && TICKET_SEVERITY_VALUES.has(value);
}
